import { useCallback, useEffect, useRef, useState } from "react";
import GpsOdometer from "@/components/GpsOdometer";
import { useNavigate, useParams } from "react-router-dom";
import WizardLayout from "@/components/WizardLayout";
import PhotoUploader from "@/components/PhotoUploader";
import SignaturePad from "@/components/SignaturePad";
import { EQUIPMENT, FUEL_LEVELS, WATER_LEVELS } from "@/components/HandoverForm";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api, formatError } from "@/lib/api";
import { toast } from "sonner";
import {
  Check,
  CheckCircle2,
  XCircle,
  KeyRound,
  MapPin,
  Loader2,
  Unlock,
} from "lucide-react";
import { Aurora } from "@/components/motion/Aurora";
import RevealTitle from "@/components/motion/RevealTitle";

const STEPS = ["Verificación", "Fotos", "Apertura", "Interior", "Firma"];
const STEP_GATES = 0;
const STEP_PHOTOS = 1;
const STEP_UNLOCK = 2;
const STEP_INTERIOR = 3;
const STEP_SIGN = 4;

const emptyEquipment = () =>
  Object.fromEntries(EQUIPMENT.map(([k]) => [k, true]));

function getPosition() {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error("Tu navegador no soporta geolocalización"));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) =>
        resolve({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
        }),
      (err) =>
        reject(
          new Error(
            err.code === 1
              ? "Debes permitir el acceso a tu ubicación para continuar"
              : "No se pudo obtener tu ubicación. Sal a cielo abierto y reintenta."
          )
        ),
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  });
}

export default function SelfPickup() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState(null); // respuesta de /self-service/state
  const [step, setStep] = useState(STEP_GATES);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [keyHint, setKeyHint] = useState("");
  const [interior, setInterior] = useState({
    km_start: "",
    fuel_level: "lleno",
    fresh_water_level: "lleno",
    interior_clean: true,
    exterior_clean: true,
    equipment: emptyEquipment(),
    interior_photos: [],
    damage_notes: "",
    damage_photos: [],
  });
  const [signature, setSignature] = useState("");
  const pollRef = useRef(null);

  const setInt = (k, v) => setInterior((d) => ({ ...d, [k]: v }));
  const setEq = (k, v) =>
    setInterior((d) => ({ ...d, equipment: { ...d.equipment, [k]: v } }));

  const loadState = useCallback(async () => {
    const { data } = await api.get(`/r/${code}/self-service/state`);
    setState(data);
    return data;
  }, [code]);

  // Carga inicial + reanudación según el estado guardado
  useEffect(() => {
    loadState()
      .then((s) => {
        const st = s.pickup?.status;
        if (st === "completed") setDone(true);
        else if (st === "unlocked") {
          setKeyHint(s.pickup.key_hint || "");
          setStep(STEP_INTERIOR);
        } else if (st === "unlock_requested") setStep(STEP_UNLOCK);
      })
      .catch((err) => {
        toast.error(formatError(err));
        navigate(`/reserva/${code}`);
      });
  }, [code, navigate, loadState]);

  // Polling mientras esperamos que el propietario confirme la apertura
  const waiting = step === STEP_UNLOCK && state?.pickup?.status === "unlock_requested";
  useEffect(() => {
    if (!waiting) return undefined;
    pollRef.current = setInterval(async () => {
      try {
        const s = await loadState();
        if (s.pickup?.status === "unlocked") {
          setKeyHint(s.pickup.key_hint || "");
          toast.success("¡Furgoneta abierta!");
          setStep(STEP_INTERIOR);
        }
      } catch {
        // el siguiente tick reintenta
      }
    }, 5000);
    return () => clearInterval(pollRef.current);
  }, [waiting, loadState]);

  const requestUnlock = async () => {
    setLoading(true);
    try {
      const location = await getPosition();
      const { data } = await api.post(`/r/${code}/self-service/request-unlock`, {
        location,
        photos,
      });
      toast.success(
        `Verificado a ${Math.round(data.distance_m)} m del punto. Avisando al propietario...`
      );
      await loadState();
    } catch (err) {
      toast.error(err?.response ? formatError(err) : err.message);
    } finally {
      setLoading(false);
    }
  };

  const completePickup = async () => {
    setLoading(true);
    try {
      await api.post(`/r/${code}/self-service/complete-pickup`, {
        ...interior,
        km_start: Number(interior.km_start),
        signature,
      });
      setDone(true);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setLoading(false);
    }
  };

  const isStepValid = () => {
    switch (step) {
      case STEP_GATES:
        return Boolean(state?.all_ok);
      case STEP_PHOTOS:
        return photos.length >= (state?.min_exterior_photos ?? 6);
      case STEP_UNLOCK:
        return state?.pickup?.status === "unlocked";
      case STEP_INTERIOR:
        return (
          interior.km_start !== "" &&
          Number(interior.km_start) >= 0 &&
          interior.interior_photos.length >= (state?.min_interior_photos ?? 2)
        );
      case STEP_SIGN:
        return Boolean(signature);
      default:
        return false;
    }
  };

  const onNext = async () => {
    if (step === STEP_SIGN) {
      await completePickup();
      return;
    }
    setStep((s) => Math.min(STEPS.length - 1, s + 1));
  };

  const onBack = () => {
    if (step === STEP_GATES) {
      navigate(`/reserva/${code}`);
      return;
    }
    // Una vez pedida/confirmada la apertura no se retrocede a pasos previos
    if (step > STEP_UNLOCK) return;
    setStep((s) => Math.max(0, s - 1));
  };

  if (!state) return null;

  if (done) {
    return (
      <div className="aurora-page relative isolate min-h-screen bg-forest-deep flex items-center justify-center p-6">
        <Aurora variant="dark" fixed />
        <div className="bg-white border border-sand rounded-2xl p-8 max-w-md w-full text-center fade-in" data-testid="sp-done">
          <div className="h-14 w-14 rounded-full bg-ok text-white flex items-center justify-center mx-auto mb-4">
            <Check className="h-7 w-7" />
          </div>
          <RevealTitle as="h2" immediate className="font-display text-2xl font-bold text-charcoal mb-2">
            ¡Buen viaje!
          </RevealTitle>
          <p className="text-warmgray mb-6">
            Recogida completada y contrato enviado a tu email. Recuerda: a la
            vuelta, deja la llave en su funda de la guantera antes de cerrar.
          </p>
          <button
            onClick={() => navigate(`/reserva/${code}`)}
            className="bg-forest-deep hover:bg-forest text-white rounded-lg px-6 py-3 font-medium"
            data-testid="sp-done-btn"
          >
            Volver a mi reserva
          </button>
        </div>
      </div>
    );
  }

  const unlocked = state.pickup?.status === "unlocked";

  return (
    <WizardLayout
      title={
        step === STEP_GATES ? "Verificación previa" :
        step === STEP_PHOTOS ? "Fotos del exterior" :
        step === STEP_UNLOCK ? "Apertura de la furgoneta" :
        step === STEP_INTERIOR ? "Estado interior" :
        "Firma de la entrega"
      }
      step={step}
      totalSteps={STEPS.length}
      stepLabel={`Recogida autónoma · ${STEPS[step]}`}
      onBack={onBack}
      onNext={onNext}
      nextLabel={step === STEP_UNLOCK && !unlocked ? "Esperando apertura..." : undefined}
      nextDisabled={!isStepValid()}
      isLastStep={step === STEP_SIGN}
      loading={loading}
    >
      {step === STEP_GATES && (
        <div className="space-y-3 max-w-xl" data-testid="sp-gates">
          <p className="text-warmgray mb-2">
            Antes de abrir la furgoneta comprobamos que todo está en orden:
          </p>
          {state.gates.map((g) => (
            <div
              key={g.key}
              className={`flex items-center gap-3 rounded-lg border p-3.5 text-sm ${
                g.ok
                  ? "bg-ok/10 border-ok/30 text-charcoal"
                  : "bg-danger/10 border-danger/30 text-danger"
              }`}
              data-testid={`sp-gate-${g.key}`}
            >
              {g.ok ? (
                <CheckCircle2 className="h-5 w-5 text-ok shrink-0" />
              ) : (
                <XCircle className="h-5 w-5 shrink-0" />
              )}
              <div>
                <div className="font-medium">{g.label}</div>
                {g.detail && !g.ok && <div className="text-xs mt-0.5">{g.detail}</div>}
              </div>
            </div>
          ))}
          {!state.all_ok && (
            <p className="text-xs text-warmgray-light">
              Resuelve los puntos en rojo (o contacta con nosotros) y recarga
              esta página.
            </p>
          )}
        </div>
      )}

      {step === STEP_PHOTOS && (
        <div className="space-y-4 max-w-xl">
          <p className="text-warmgray">
            Documenta el estado exterior <strong>antes de abrir</strong>: los 4
            lados, el techo y las ruedas. Estas fotos te protegen ante
            cualquier daño previo.
          </p>
          <PhotoUploader
            label={`Fotos exteriores (${photos.length}/${state.min_exterior_photos} mínimo)`}
            value={photos}
            onChange={setPhotos}
            multiple
            uploadCode={code}
            testid="sp-photos"
          />
        </div>
      )}

      {step === STEP_UNLOCK && (
        <div className="space-y-5 max-w-xl" data-testid="sp-unlock-step">
          {state.pickup?.status !== "unlock_requested" && !unlocked && (
            <>
              <p className="text-warmgray">
                Ponte junto a la furgoneta y pulsa el botón: comprobaremos tu
                ubicación y avisaremos al propietario para que la abra a
                distancia.
              </p>
              <button
                onClick={requestUnlock}
                disabled={loading}
                className="w-full bg-forest-deep hover:bg-forest text-white rounded-xl p-5 flex items-center justify-center gap-3 font-semibold text-lg disabled:opacity-60"
                data-testid="sp-request-unlock-btn"
              >
                {loading ? (
                  <Loader2 className="h-6 w-6 animate-spin" />
                ) : (
                  <MapPin className="h-6 w-6" />
                )}
                Estoy junto a la furgoneta — Solicitar apertura
              </button>
              <p className="text-xs text-warmgray-light">
                Necesitamos tu ubicación una única vez, solo para verificar que
                estás en el punto de recogida.
              </p>
            </>
          )}

          {state.pickup?.status === "unlock_requested" && !unlocked && (
            <div className="bg-white border border-sand rounded-xl p-6 text-center" data-testid="sp-waiting">
              <Loader2 className="h-10 w-10 text-forest-deep animate-spin mx-auto mb-3" />
              <h3 className="font-display text-xl font-bold text-charcoal mb-1">
                Avisando al propietario...
              </h3>
              <p className="text-sm text-warmgray">
                Todo verificado. En cuanto abra la furgoneta a distancia, esta
                pantalla se actualizará sola (suele tardar 1-3 minutos). No
                cierres la página.
              </p>
              {state.pickup?.owner_phone && (
                <p className="mt-4 text-sm text-charcoal" data-testid="sp-owner-phone">
                  ¿Llevas más de 10 minutos esperando? Llámanos:{" "}
                  <a href={`tel:${state.pickup.owner_phone.replace(/\s/g, "")}`} className="font-semibold underline">
                    {state.pickup.owner_phone}
                  </a>
                </p>
              )}
            </div>
          )}

          {unlocked && (
            <div className="bg-ok/10 border border-ok/30 rounded-xl p-6" data-testid="sp-unlocked">
              <div className="flex items-center gap-3 mb-3">
                <Unlock className="h-7 w-7 text-ok" />
                <h3 className="font-display text-xl font-bold text-charcoal">
                  ¡Furgoneta abierta!
                </h3>
              </div>
              <div className="flex items-start gap-3 bg-white border border-sand rounded-lg p-4">
                <KeyRound className="h-5 w-5 text-terracotta shrink-0 mt-0.5" />
                <p className="text-sm text-charcoal">{keyHint || state.pickup?.key_hint}</p>
              </div>
              <p className="text-xs text-warmgray mt-3">
                Continúa para registrar el estado interior y firmar la entrega.
              </p>
            </div>
          )}
        </div>
      )}

      {step === STEP_INTERIOR && (
        <div className="space-y-5 max-w-2xl">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Km del cuadro</Label>
              <Input
                type="number"
                min="0"
                value={interior.km_start}
                onChange={(e) => setInt("km_start", e.target.value)}
                className="mt-2 bg-white border-sand"
                data-testid="sp-km"
              />
              <GpsOdometer code={code} onValue={(km) => setInt("km_start", String(km))} onFuel={(f) => setInt("fuel_level", f)} current={interior.km_start} testId="sp-gps" />
            </div>
            <div>
              <Label>Combustible</Label>
              <Select value={interior.fuel_level} onValueChange={(v) => setInt("fuel_level", v)}>
                <SelectTrigger className="mt-2 bg-white border-sand" data-testid="sp-fuel">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FUEL_LEVELS.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Agua limpia</Label>
              <Select value={interior.fresh_water_level} onValueChange={(v) => setInt("fresh_water_level", v)}>
                <SelectTrigger className="mt-2 bg-white border-sand" data-testid="sp-water">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {WATER_LEVELS.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            {[["interior_clean", "Interior limpio"], ["exterior_clean", "Exterior limpio"]].map(([key, label]) => (
              <label key={key} className="flex items-center gap-3 bg-white border border-sand rounded-lg p-3 cursor-pointer text-sm">
                <Checkbox checked={interior[key]} onCheckedChange={(v) => setInt(key, Boolean(v))} data-testid={`sp-${key}`} />
                <span>{label}</span>
              </label>
            ))}
          </div>

          <div>
            <h4 className="font-display font-semibold text-charcoal mb-2">
              Equipamiento a bordo
            </h4>
            <p className="text-xs text-warmgray mb-2">
              Desmarca lo que falte — quedará registrado en el contrato.
            </p>
            <div className="grid sm:grid-cols-2 gap-2">
              {EQUIPMENT.map(([key, label]) => (
                <label key={key} className="flex items-center justify-between gap-3 bg-white border border-sand rounded-lg p-2.5 cursor-pointer text-sm">
                  <span>{label}</span>
                  <Checkbox checked={interior.equipment[key]} onCheckedChange={(v) => setEq(key, Boolean(v))} data-testid={`sp-eq-${key}`} />
                </label>
              ))}
            </div>
          </div>

          <PhotoUploader
            label={`Fotos del interior y del cuadro con los km (${interior.interior_photos.length}/${state.min_interior_photos} mínimo)`}
            value={interior.interior_photos}
            onChange={(v) => setInt("interior_photos", v)}
            multiple
            uploadCode={code}
            testid="sp-interior-photos"
          />

          <div>
            <Label>¿Ves algún daño no descrito? (opcional)</Label>
            <Textarea
              value={interior.damage_notes}
              onChange={(e) => setInt("damage_notes", e.target.value)}
              rows={3}
              placeholder="Ej. Rayón en puerta lateral derecha"
              className="mt-2 bg-white border-sand"
              data-testid="sp-damage-notes"
            />
            <div className="mt-3">
              <PhotoUploader
                label="Fotos de los daños (opcional)"
                value={interior.damage_photos}
                onChange={(v) => setInt("damage_photos", v)}
                multiple
                uploadCode={code}
                testid="sp-damage-photos"
              />
            </div>
          </div>
        </div>
      )}

      {step === STEP_SIGN && (
        <div className="space-y-4 max-w-xl">
          <p className="text-warmgray">
            Firmas que recibes el vehículo en el estado documentado en tus
            fotos y con el equipamiento marcado. El contrato con todo el
            detalle llegará a tu email.
          </p>
          <SignaturePad value={signature} onChange={setSignature} testid="sp-signature" />
        </div>
      )}
    </WizardLayout>
  );
}
